import React, { createContext, useContext, useState, useEffect, useCallback, useRef, useMemo } from 'react';
import axios from 'axios';
import logger from '../utils/logger';
import { supabase } from '../lib/supabase';

const AuthContext = createContext();
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// If supabase.auth.getSession() (or /auth/me) hangs, ProtectedRoute would spin forever.
// Settle loading after this timeout so signed-out users can Navigate to /login (I3c Part B).
const AUTH_LOADING_TIMEOUT_MS = 8000;

// Cookies must ride cross-origin requests (frontend domain → Railway backend domain)
axios.defaults.withCredentials = true;

export const AuthProvider = ({ children }) => {
  // Token lives ONLY in React state (in-memory). httpOnly cookie handles persistence across page reloads.
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(null);

  const clearAuth = useCallback(() => {
    setToken(null);
    setUser(null);
    delete axios.defaults.headers.common['Authorization'];
  }, []);

  // Restore session on page load via httpOnly cookie (no localStorage)
  const fetchUser = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      if (error.response?.status === 401) {
        logger.debug('No active session:', error);
      } else {
        logger.error('Failed to fetch user:', error);
      }
      clearAuth();
    } finally {
      setLoading(false);
    }
  }, [clearAuth]);

  // Stable ref so the mount-only effect can read `token` without it being a dependency
  const tokenRef = useRef(null);
  useEffect(() => { tokenRef.current = token; }, [token]);

  // Stable refs for callbacks used inside the mount effect
  const fetchUserRef = useRef(fetchUser);
  useEffect(() => { fetchUserRef.current = fetchUser; }, [fetchUser]);

  // Mount-only effect: restore session from Supabase or httpOnly cookie (runs exactly once)
  useEffect(() => {
    // Skip OAuth callback pages -- they handle their own auth
    if (window.location.search?.includes('code=')) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    // Safety net: always settle loading so ProtectedRoute can redirect signed-out users.
    const loadingTimeout = setTimeout(() => {
      if (!cancelled) {
        logger.debug('Auth session restore timed out; settling loading');
        setLoading(false);
      }
    }, AUTH_LOADING_TIMEOUT_MS);

    const currentToken = tokenRef.current;
    if (currentToken) {
      // Token already in memory -- just set the header and fetch user
      axios.defaults.headers.common['Authorization'] = `Bearer ${currentToken}`;
      fetchUserRef.current();
      return () => {
        cancelled = true;
        clearTimeout(loadingTimeout);
      };
    }

    // Restore session from Supabase
    const restoreSession = async () => {
      // Step 1: Check for Supabase session (race so a hang cannot block forever)
      if (supabase) {
        try {
          const { data: { session } } = await Promise.race([
            supabase.auth.getSession(),
            new Promise((_, reject) => {
              setTimeout(
                () => reject(new Error('AUTH_LOADING_TIMEOUT')),
                AUTH_LOADING_TIMEOUT_MS,
              );
            }),
          ]);
          if (cancelled) return;
          if (session?.access_token) {
            // Use Supabase token with the backend
            axios.defaults.headers.common['Authorization'] = `Bearer ${session.access_token}`;
            const meResponse = await axios.get(`${API}/auth/me`);
            if (cancelled) return;
            setToken(session.access_token);
            setUser(meResponse.data);
            setLoading(false);
            return;
          }
        } catch (e) {
          // Supabase session not available / timed out -- fall through to cookie-based restore
          logger.debug('Supabase session restore failed:', e);
        }
      }
      if (cancelled) return;
      // Fallback: backend httpOnly access_token cookie may still be valid.
      // fetchUser sets loading=false in its finally block.
      fetchUserRef.current();
    };

    restoreSession();

    // Listen for Supabase auth state changes (login/logout from other tabs, token refresh)
    let subscription;
    if (supabase) {
      const { data } = supabase.auth.onAuthStateChange((event, session) => {
        if (event === 'SIGNED_OUT') {
          clearAuth();
        } else if (event === 'PASSWORD_RECOVERY') {
          // Supabase recovery link landed on the root — redirect to the reset page.
          // Preserve the locale of the current pathname (/en/* → /en/reset-password,
          // otherwise default to French /reset-password).
          const localePrefix = window.location.pathname.startsWith('/en') ? '/en' : '';
          window.location.replace(`${localePrefix}/reset-password?mode=supabase`);
        } else if (event === 'TOKEN_REFRESHED' && session?.access_token) {
          setToken(session.access_token);
          axios.defaults.headers.common['Authorization'] = `Bearer ${session.access_token}`;
        } else if (event === 'SIGNED_IN' && session?.access_token) {
          // Fires when Supabase exchanges the OAuth ?code= for a session (detectSessionInUrl).
          // Set the token and fetch the user from the backend so AuthCallback can proceed.
          const accessToken = session.access_token;
          setToken(accessToken);
          axios.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
          axios.get(`${API}/auth/me`)
            .then((res) => setUser(res.data))
            .catch((err) => {
              logger.error('Failed to fetch user after SIGNED_IN event:', err);
              clearAuth();
            });
        }
      });
      subscription = data?.subscription;
    }

    return () => {
      cancelled = true;
      clearTimeout(loadingTimeout);
      if (subscription) {
        subscription.unsubscribe();
      }
    };
  }, []); // intentional: mount-only, deps managed via refs above

  const login = useCallback(async (email, password, totpCode = null, remember = true) => {
    const payload = { email, password, remember };
    if (totpCode) payload.totp_code = totpCode;

    const response = await axios.post(`${API}/auth/login`, payload);

    if (response.data.requires_2fa) {
      return { requires_2fa: true };
    }

    if (response.data.requires_email_verification) {
      return { requires_email_verification: true };
    }

    const { access_token, user: userData } = response.data;

    setToken(access_token);
    setUser(userData);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;

    // Persist Supabase session so getSession() works after page reload / browser restart.
    if (supabase && response.data.refresh_token) {
      await supabase.auth.setSession({
        access_token,
        refresh_token: response.data.refresh_token,
      });
    }

    return userData;
  }, []);

  const loginWithGoogle = useCallback(async (redirectPath = '/auth/callback') => {
    const redirectUrl = window.location.origin + redirectPath;
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: redirectUrl },
    });
    if (error) {
      logger.error('Google OAuth init failed:', error);
      throw error;
    }
  }, []);

  const register = useCallback(async (data) => {
    const response = await axios.post(`${API}/auth/register`, data);
    const { access_token, refresh_token, user: userData } = response.data;

    // Backend no longer auto-signs in after registration — user must verify email first.
    if (!access_token) {
      return { verification_required: true, user: userData };
    }

    setToken(access_token);
    setUser(userData);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;

    // Persist Supabase session so getSession() works after page reload
    if (supabase && refresh_token) {
      await supabase.auth.setSession({ access_token, refresh_token });
    }

    return userData;
  }, []);

  const logout = useCallback(async () => {
    try {
      await axios.post(`${API}/auth/logout`, {});
    } catch (error) {
      logger.error('Logout error:', error);
    }
    // Sign out of Supabase as well (clears Supabase session storage)
    if (supabase) {
      try {
        await supabase.auth.signOut();
      } catch (e) {
        logger.debug('Supabase signOut error (non-fatal):', e);
      }
    }
    clearAuth();
  }, [clearAuth]);

  const logoutAllDevices = useCallback(async () => {
    try {
      await axios.post(`${API}/auth/logout-all`);
    } catch (error) {
      logger.error('Logout all devices error:', error);
    }
    if (supabase) {
      try {
        await supabase.auth.signOut();
      } catch (e) {
        logger.debug('Supabase signOut error (non-fatal):', e);
      }
    }
    clearAuth();
  }, [clearAuth]);

  // Memoized context value -- prevents unnecessary re-renders in all consumers
  const contextValue = useMemo(() => ({
    user,
    loading,
    token,
    login,
    loginWithGoogle,
    register,
    logout,
    logoutAllDevices
  }), [user, loading, token, login, loginWithGoogle, register, logout, logoutAllDevices]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
